import React, { useState, useEffect, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { Transition } from '@headlessui/react';
import {
  XMarkIcon,
  CheckIcon,
  ExclamationTriangleIcon,
  ShieldExclamationIcon,
  InformationCircleIcon,
  DocumentTextIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import { Notification } from '../../types/notifications';
import { getNotifications, markNotificationAsRead, markAllNotificationsAsRead } from '../../services/notifications';

interface NotificationPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

const NotificationPanel: React.FC<NotificationPanelProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadNotifications();
    }
  }, [isOpen]);

  const loadNotifications = async () => {
    setLoading(true);
    try {
      const data = await getNotifications(20);
      setNotifications(data);
    } catch (error) {
      console.error('Erro ao carregar notificações:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (notificationId: string) => {
    try {
      await markNotificationAsRead(notificationId);
      setNotifications(prev => prev.map(n => (n.id === notificationId ? { ...n, read: true } : n)));
    } catch (error) {
      console.error('Erro ao marcar como lida:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await markAllNotificationsAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch (error) {
      console.error('Erro ao marcar todas como lidas:', error);
    }
  };

  const handleNotificationClick = async (notification: Notification) => {
    // Marcar como lida se necessário
    if (!notification.read) {
      handleMarkAsRead(notification.id);
    }

    // Fechar painel
    onClose();

    // Redirecionar baseado no tipo de entidade
    const { related_entity_type, related_entity_id } = notification.metadata || {};

    if (related_entity_type && related_entity_id) {
      switch (related_entity_type) {
        case 'ticket':
          navigate(`/tickets/${related_entity_id}`);
          break;
        case 'event':
          navigate(`/events/${related_entity_id}`);
          break;
        case 'investigation':
          navigate(`/domain-investigation/${related_entity_id}`);
          break;
        case 'report':
          navigate('/reports');
          break;
        default:
          break;
      }
    }
  };

  const getNotificationIcon = (type: string, severity: string) => {
    const iconClass = `h-5 w-5 ${getSeverityColor(severity)}`;

    switch (type) {
      case 'takedown':
        return <CheckIcon className={iconClass} />;
      case 'security_alert':
        return <ShieldExclamationIcon className={iconClass} />;
      case 'investigation_update':
        return <MagnifyingGlassIcon className={iconClass} />;
      case 'report_ready':
        return <DocumentTextIcon className={iconClass} />;
      case 'leaked_credentials':
        return <ExclamationTriangleIcon className={iconClass} />;
      case 'system_alert':
        return <InformationCircleIcon className={iconClass} />;
      default:
        return <InformationCircleIcon className={iconClass} />;
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical':
        return 'text-red-600';
      case 'high':
        return 'text-orange-600';
      case 'medium':
        return 'text-yellow-600';
      case 'low':
        return 'text-blue-600';
      default:
        return 'text-gray-600';
    }
  };

  const formatTimeAgo = (timestamp: Date | string) => {
    const now = new Date();
    const notificationTime = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;
    const diffInMs = now.getTime() - notificationTime.getTime();
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));

    if (diffInDays > 0) {
      return `${diffInDays} dia${diffInDays > 1 ? 's' : ''} atrás`;
    } else if (diffInHours > 0) {
      return `${diffInHours} hora${diffInHours > 1 ? 's' : ''} atrás`;
    } else if (diffInMinutes > 0) {
      return `${diffInMinutes} minuto${diffInMinutes > 1 ? 's' : ''} atrás`;
    } else {
      return 'Agora mesmo';
    }
  };

  return (
    <Transition
      show={isOpen}
      as={Fragment}
      enter='transition ease-out duration-200'
      enterFrom='opacity-0 translate-x-full'
      enterTo='opacity-100 translate-x-0'
      leave='transition ease-in duration-150'
      leaveFrom='opacity-100 translate-x-0'
      leaveTo='opacity-0 translate-x-full'
    >
      <div className='fixed inset-y-0 right-0 z-50 w-96 bg-white dark:bg-gray-800 shadow-xl border-l border-gray-200 dark:border-gray-700'>
        {/* Header */}
        <div className='flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700'>
          <h2 className='text-lg font-semibold text-gray-900 dark:text-gray-100'>Notificações</h2>
          <div className='flex items-center space-x-2'>
            <button
              onClick={handleMarkAllAsRead}
              className='text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300'
            >
              Marcar todas como lidas
            </button>
            <button
              onClick={onClose}
              className='text-gray-400 hover:text-gray-600 dark:text-gray-300 dark:hover:text-gray-100'
            >
              <XMarkIcon className='h-6 w-6' />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className='flex-1 overflow-y-auto'>
          {loading ? (
            <div className='flex items-center justify-center p-8'>
              <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600'></div>
            </div>
          ) : notifications.length === 0 ? (
            <div className='flex flex-col items-center justify-center p-8 text-gray-500 dark:text-gray-400'>
              <InformationCircleIcon className='h-12 w-12 mb-4' />
              <p>Nenhuma notificação encontrada</p>
            </div>
          ) : (
            <div className='divide-y divide-gray-200 dark:divide-gray-700'>
              {notifications.map(notification => (
                <div
                  key={notification.id}
                  className={`p-4 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer ${!notification.read ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                    }`}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div className='flex items-start space-x-3'>
                    <div className='flex-shrink-0 mt-1'>
                      {getNotificationIcon(notification.type, notification.severity)}
                    </div>
                    <div className='flex-1 min-w-0'>
                      <div className='flex items-center justify-between'>
                        <p
                          className={`text-sm font-medium ${!notification.read ? 'text-gray-900 dark:text-gray-100' : 'text-gray-600 dark:text-gray-400'
                            }`}
                        >
                          {notification.title}
                        </p>
                        {!notification.read && <div className='w-2 h-2 bg-blue-600 rounded-full flex-shrink-0'></div>}
                      </div>
                      <p
                        className={`text-sm mt-1 ${!notification.read ? 'text-gray-700 dark:text-gray-300' : 'text-gray-500 dark:text-gray-500'
                          }`}
                      >
                        {notification.message}
                      </p>
                      <p className='text-xs text-gray-500 dark:text-gray-400 mt-2'>
                        {formatTimeAgo(notification.timestamp)}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Transition>
  );
};

export default NotificationPanel;
